/**
 * Calculates all totals for vendor invoices. Included in the VendorInvoice
 * service and includes the service for vendor invoice item totals
 */
export function VendorInvoiceTotalsService (sessionObjAPI, VendorInvoiceItemTotals) {

  var VendorInvoiceTotals = function (invoice) {
    this.company     = sessionObjAPI.company();
    this.hasGroupTax = false;
    this.info        = sessionObjAPI.currency();
    this.invoice     = invoice;
    this.itemTotals  = new VendorInvoiceItemTotals(invoice);
  };

  /**
   * Determines whether or not the invoice is closed based on status
   * @return {boolean} true if closed, false if open
   */
  VendorInvoiceTotals.prototype.closed = function () {
    var self = this;

    return self.invoice.invoice_status === 'P' || self.invoice.invoice_status === 'C';
  };

  /**
   * If the flag "calculate_tax_on_subtotal" is set, then tax needs to be calculated
   * at the subtotal level, not just the total of all the item's tax calclulations.
   * This handles that form of calculations
   * @return {object} The Big Decimal value for the calculated tax
   */
  VendorInvoiceTotals.prototype.calculateTaxBySubtotal = function () {
    var self = this;
    var subtotal = BigDecimal.ZERO;
    var tax;
    var taxRate = new BigDecimal(self.company.purchases_tax_rate).movePointLeft(2);

    // loop through the items and get the total of them, without taxes
    angular.forEach(self.invoice.vendor_invoice_items_attributes, function (item){
      var cost;
      var unit_cost = BigDecimal.ZERO;
      var quantity = BigDecimal.ZERO;

      // We only need the items where tax is included
      if (!item.gst_free) {
        cost = self.invoice.price_includes_tax ? item.cost_including_tax : item.unit_cost;
        unit_cost = new BigDecimal( cost ? cost.toString(): '0' ).setScale(4, BigDecimal.ROUND_HALF_UP);
        quantity = new BigDecimal( item.quantity && item.quantity !== '-' ? item.quantity.toString(): '0' ).setScale(2, BigDecimal.ROUND_HALF_UP);
        subtotal = subtotal.add(unit_cost.multiply(quantity));
      }
    });

    // Now let's calculate the tax based on the subtotal of items

    // If the price includes tax, extract the tax from the total
    if (self.invoice.price_includes_tax) {
      tax = subtotal
        .subtract( subtotal.divide( BigDecimal.ONE.add(taxRate) ) )
          .setScale(2, BigDecimal.ROUND_HALF_UP);
    }

    // If not, calculate the tax based on the tax rate
    else {
      tax = subtotal
        .multiply(taxRate)
          .setScale(2, BigDecimal.ROUND_HALF_UP);
    }

    return tax;
  };

  /**
   * Calculates the balance due for the whole invoice
   * @return {string} the balance due for the invoice
   */
  VendorInvoiceTotals.prototype.balance = function () {
    var self = this;
    var total = new BigDecimal( self.invoice.savedTotal.toString() ).setScale(2, BigDecimal.ROUND_HALF_UP);
    var balance = new BigDecimal( self.invoice.savedBalance.toString() ).setScale(2, BigDecimal.ROUND_HALF_UP);
    var totalCalc = new BigDecimal( self.total() ).setScale(2, BigDecimal.ROUND_HALF_UP);

    // If the invoice is close, return what is sent from the server
    if (self.closed()) {
      return self.invoice.balance_due ? self.invoice.balance_due.toString(): '0';
    }

    // If the balance due is zero and not closed, then it is new so return the total
    if (balance.isZero()) {
      return totalCalc.toString();
    }

    // If we have a balance from the server, use it to calculate the new balance
    else {
      return totalCalc.subtract(total).add(balance).toString();
    }
  };

  /**
   * Calculates the tax for the whole invoice
   * @return {string} The total tax for the invoice
   */
  VendorInvoiceTotals.prototype.tax = function () {
    var self = this;
    var tax = BigDecimal.ZERO;
    var freight = self.company.tax_freight ? new BigDecimal(self.invoice.freight ? self.invoice.freight.toString(): '0').setScale(2, BigDecimal.ROUND_HALF_UP): BigDecimal.ZERO;
    var taxRate = new BigDecimal(self.company.purchases_tax_rate).movePointLeft(2);

    // If the invoice is closed, return the amount from the server
    if (self.closed()) {
      return self.invoice.gst || '0';
    }

    // If the flag is set, then we need to calculate the tax based on the subtotal (invoice level)
    else if (self.company.calculate_tax_on_subtotal) {
      tax = self.calculateTaxBySubtotal();
    }

    // If not, just add up the total taxes for each item (item level)
    else {
      angular.forEach(self.invoice.vendor_invoice_items_attributes, function (item){
        var taxCalc = new BigDecimal( self.itemTotals.tax(item) ).setScale(2, BigDecimal.ROUND_HALF_UP);
        tax = tax.add(taxCalc);
      });
    }

    // Dont forget the freight amount
    return tax.add(freight.multiply(taxRate).setScale(2, BigDecimal.ROUND_HALF_UP)).toString();
  };

  /**
   * Calculates the subtotal for the invoice
   * @return {string} The subtotal amount
   */
  VendorInvoiceTotals.prototype.subtotal = function () {
    var self = this;
    var subtotal = BigDecimal.ZERO;
    var total = BigDecimal.ZERO
    var tax = BigDecimal.ZERO;
    var taxableTotal = BigDecimal.ZERO;
    var taxRate = new BigDecimal(self.company.purchases_tax_rate).movePointLeft(2);

    // If the invoice is closed, no need to do any calculations
    if (self.closed()) {
      return self.invoice.subtotal ? self.invoice.subtotal.toString(): '0';
    }

    // If the price includes tax then we need to extract the tax from each item for our subtotal
    else if (self.invoice.price_includes_tax && !self.company.calculate_tax_on_subtotal) {
      angular.forEach(self.invoice.vendor_invoice_items_attributes, function (item){
        var taxCalc = new BigDecimal( self.itemTotals.tax(item) );
        var itemTotal = new BigDecimal( self.itemTotals.total(item) );

        tax = tax.add(taxCalc);
        total = total.add(itemTotal);
      });

      subtotal = total.subtract(tax);
    }

    // if the calculate tax flag is set, we need to get the tax out on the invoice level
    else if (self.invoice.price_includes_tax && self.company.calculate_tax_on_subtotal) {
      angular.forEach(self.invoice.vendor_invoice_items_attributes, function (item) {
        var itemTotal = new BigDecimal( self.itemTotals.total(item) );

        total = total.add(itemTotal);

        if (!item.gst_free) {
          taxableTotal = taxableTotal.add(itemTotal);
        }
      });

      tax = taxableTotal
        .subtract( taxableTotal.divide( BigDecimal.ONE.add(taxRate) ) )
          .setScale(2, BigDecimal.prototype.ROUND_HALF_UP);

      subtotal = total.subtract(tax);

    }

    // If not, just add up each items total before taxes
    else {
      angular.forEach(self.invoice.vendor_invoice_items_attributes, function (item){
        var unit_cost = new BigDecimal( item.unit_cost ? item.unit_cost.toString(): '0' ).setScale(4, BigDecimal.ROUND_HALF_UP);
        var quantity = new BigDecimal( item.quantity ? item.quantity.toString(): '0' ).setScale(2, BigDecimal.ROUND_HALF_UP);

        subtotal = subtotal.add( unit_cost.multiply(quantity) );
      });
    }

    return subtotal ? subtotal.toString(): '0';
  };

  /**
   * Calculate the total of the invoice
   * @return {string} The total for the invoice
   */
  VendorInvoiceTotals.prototype.total = function () {
    var self = this;
    var total = new BigDecimal( self.subtotal() ).setScale(2, BigDecimal.ROUND_HALF_UP);
    var tax = new BigDecimal( self.tax() ).setScale(2, BigDecimal.ROUND_HALF_UP);
    var freight = new BigDecimal( self.invoice.freight ? self.invoice.freight.toString(): '0' ).setScale(2, BigDecimal.ROUND_HALF_UP);
    var rounding = new BigDecimal( self.invoice.rounding ? self.invoice.rounding.toString(): '0').setScale(2, BigDecimal.ROUND_HALF_UP);

    // If the invoice is closed, no need to do the calculation
    if (self.closed()) {
      return self.invoice.total ? self.invoice.total.toString(): '0';
    }

    // Dont forget the tax and the freight amount
    return total.add(tax).add(freight).add(rounding).toString();
  };

  return VendorInvoiceTotals;
}